import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox, type EntityOption } from '@/components/shared/entity-combobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchField } from '@/components/ui/switch';
import type { MemberPickerOption } from '@/pages/pastoral/use-member-picker';
import { cn } from '@/utils/cn';

export interface SacramentPersonDetails {
  motherMemberId: string | null;
  motherName: string;
  fatherMemberId: string | null;
  fatherName: string;
  photoKey: string | null;
  photoPreviewUrl: string | null;
  certificatePhotoKey: string | null;
  certificatePhotoPreviewUrl: string | null;
  createMember: boolean;
}

export interface SacramentPersonRow extends SacramentPersonDetails {
  key: string;
  memberId: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  certificateNumber: string;
}

export const emptySacramentPersonDetails = (): SacramentPersonDetails => ({
  motherMemberId: null,
  motherName: '',
  fatherMemberId: null,
  fatherName: '',
  photoKey: null,
  photoPreviewUrl: null,
  certificatePhotoKey: null,
  certificatePhotoPreviewUrl: null,
  createMember: false,
});

export const emptySacramentPersonRow = (): SacramentPersonRow => ({
  key: crypto.randomUUID(),
  memberId: null,
  firstName: '',
  middleName: '',
  lastName: '',
  certificateNumber: '',
  ...emptySacramentPersonDetails(),
});

export const composeSacramentPersonName = (row: {
  firstName: string;
  middleName: string;
  lastName: string;
}) => [row.firstName, row.middleName, row.lastName].map((part) => part.trim()).filter(Boolean).join(' ');

export const splitSacramentPersonName = (
  personName: string,
): { firstName: string; middleName: string; lastName: string } => {
  const parts = personName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0] ?? '', middleName: '', lastName: parts[0] ?? '' };
  return {
    firstName: parts[0] ?? '',
    lastName: parts[parts.length - 1] ?? '',
    middleName: parts.slice(1, -1).join(' '),
  };
};

export const sacramentPersonHasDetails = (details: SacramentPersonDetails) =>
  Boolean(
    details.motherMemberId ||
      details.motherName.trim() ||
      details.fatherMemberId ||
      details.fatherName.trim() ||
      details.photoKey ||
      details.certificatePhotoKey ||
      details.createMember,
  );

interface SacramentPeopleFieldsProps {
  rows: SacramentPersonRow[];
  onChange: (rows: SacramentPersonRow[]) => void;
  memberOptions: MemberPickerOption[];
  memberLoading: boolean;
  onMemberQueryChange: (query: string) => void;
  certificateLabel: string;
  clearLabel: string;
  hint: string;
  allowCreateMember?: boolean;
  onOpenDetails: (key: string) => void;
}

/** One track list for the header and every row, shared with subgrid so they cannot drift. */
const peopleColumns =
  'md:grid md:grid-cols-[2.5rem_minmax(0,1.35fr)_repeat(3,minmax(0,1fr))_minmax(0,0.9fr)_8.75rem] md:gap-x-2';
const peopleSubgrid = 'md:col-span-full md:grid md:grid-cols-subgrid md:items-center md:px-3';

export const SacramentPeopleFields = ({
  rows,
  onChange,
  memberOptions,
  memberLoading,
  onMemberQueryChange,
  certificateLabel,
  clearLabel,
  hint,
  allowCreateMember = false,
  onOpenDetails,
}: SacramentPeopleFieldsProps) => {
  const { t } = useTranslation('sacraments');

  const updateRow = (key: string, patch: Partial<SacramentPersonRow>) => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const unnamedRows = rows.filter((row) => !row.memberId);
  const addAllMembers =
    unnamedRows.length > 0 && unnamedRows.every((row) => row.createMember);

  const selectedOption = (row: SacramentPersonRow): EntityOption | null => {
    if (!row.memberId) return null;
    return (
      memberOptions.find((option) => option.id === row.memberId) ?? {
        id: row.memberId,
        label: composeSacramentPersonName(row),
      }
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-body-sm font-medium text-fg-primary">{t('people.title')}</p>
          <p className="text-caption text-fg-secondary">{hint}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          leadingIcon={<Plus className="size-4" aria-hidden />}
          onClick={() => onChange([...rows, emptySacramentPersonRow()])}
        >
          {t('people.addPerson')}
        </Button>
      </div>

      {allowCreateMember ? (
        <SwitchField
          id="sacrament-create-members-all"
          label={t('people.createMember.allLabel')}
          description={t('people.createMember.allDescription')}
          checked={addAllMembers}
          disabled={unnamedRows.length === 0}
          onCheckedChange={(checked) =>
            onChange(
              rows.map((row) =>
                row.memberId ? { ...row, createMember: false } : { ...row, createMember: checked },
              ),
            )
          }
        />
      ) : null}

      <div className={cn('overflow-hidden rounded-lg border border-border', peopleColumns)}>
        <div
          className={cn(
            'hidden border-b border-border bg-surface-sunken py-2 text-caption font-medium text-fg-secondary',
            peopleSubgrid,
          )}
        >
          <span aria-hidden>#</span>
          <span className="min-w-0">{t('fields.member')}</span>
          <span className="min-w-0">{t('people.lastName')}</span>
          <span className="min-w-0">{t('people.firstName')}</span>
          <span className="min-w-0">{t('people.middleName')}</span>
          <span className="min-w-0">{certificateLabel}</span>
          <span className="sr-only">{t('people.extraDetails')}</span>
        </div>

        <div className="contents">
          {rows.map((row, index) => (
            <div
              key={row.key}
              className={cn(
                'grid grid-cols-1 items-end gap-2 border-b border-border px-3 py-2 last:border-b-0',
                peopleSubgrid,
              )}
            >
              <div className="flex h-10 items-center text-body-sm font-medium tabular-nums text-fg-secondary">
                <span className="md:sr-only">{t('people.personNumber', { number: index + 1 })}</span>
                <span className="hidden md:inline" aria-hidden>
                  {index + 1}
                </span>
              </div>

              <div className="min-w-0 space-y-1.5 md:space-y-0">
                <Label className="md:sr-only">{t('fields.member')}</Label>
                <EntityCombobox
                  value={row.memberId}
                  onChange={(id) => {
                    const option = id ? memberOptions.find((item) => item.id === id) : undefined;
                    updateRow(row.key, {
                      memberId: id,
                      ...(id
                        ? {
                            createMember: false,
                            firstName: option?.firstName ?? row.firstName,
                            middleName: option?.middleName ?? row.middleName,
                            lastName: option?.lastName ?? row.lastName,
                          }
                        : {}),
                    });
                  }}
                  options={memberOptions}
                  selected={selectedOption(row)}
                  onQueryChange={onMemberQueryChange}
                  loading={memberLoading}
                  placeholder={t('memberPicker.placeholder')}
                  emptyMessage={t('memberPicker.empty')}
                  clearLabel={clearLabel}
                  popoverPortal="dialog"
                  className="w-full min-w-0"
                />
              </div>

              <NameInput
                label={t('people.lastName')}
                value={row.lastName}
                readOnly={Boolean(row.memberId)}
                required={!row.memberId}
                onChange={(lastName) => updateRow(row.key, { lastName })}
              />
              <NameInput
                label={t('people.firstName')}
                value={row.firstName}
                readOnly={Boolean(row.memberId)}
                required={!row.memberId}
                onChange={(firstName) => updateRow(row.key, { firstName })}
              />
              <NameInput
                label={t('people.middleName')}
                value={row.middleName}
                readOnly={Boolean(row.memberId)}
                onChange={(middleName) => updateRow(row.key, { middleName })}
              />

              <div className="min-w-0 space-y-1.5 md:space-y-0">
                <Label className="md:sr-only">{certificateLabel}</Label>
                <Input
                  className="w-full min-w-0"
                  value={row.certificateNumber}
                  onChange={(event) =>
                    updateRow(row.key, { certificateNumber: event.target.value })
                  }
                />
              </div>

              <div className="flex min-w-0 items-center justify-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant={sacramentPersonHasDetails(row) ? 'secondary' : 'outline'}
                  onClick={() => onOpenDetails(row.key)}
                >
                  {t('people.extraDetails')}
                </Button>
                {rows.length > 1 ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={t('people.removePerson')}
                    onClick={() => onChange(rows.filter((item) => item.key !== row.key))}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const NameInput = ({
  label,
  value,
  readOnly,
  required = false,
  onChange,
}: {
  label: string;
  value: string;
  readOnly: boolean;
  required?: boolean;
  onChange: (value: string) => void;
}) => (
  <div className="min-w-0 space-y-1.5 md:space-y-0">
    <Label className="md:sr-only">{label}</Label>
    <Input
      className="w-full min-w-0"
      value={value}
      readOnly={readOnly}
      aria-required={required}
      aria-label={label}
      placeholder={readOnly ? undefined : label}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);
